<template>
  <BittsContainer class="team-counter-card">
    <div class="team-counter-card__title">
      <h2>{{ title }}</h2>
      <component
        :is="buttonInfo.tooltipText ? BittsTooltip : 'div'"
        placement="bottomRight"
        :class="{
          invisible: !Object.values(buttonInfo || {}).length,
          'h-32': true,
        }"
      >
        <BittsButton
          v-bind="buttonInfo"
          size="small"
          @click="buttonInfo?.click"
        />
        <template #title>
          {{ buttonInfo.tooltipText }}
        </template>
      </component>
    </div>
    <div class="flex items-center font-bold text-xl">
      {{ count }}
      <span v-if="limit !== null" class="text-neutral-text-placeholder">
        /{{ limit }}
      </span>
    </div>
    <BittsTag v-if="requests.length" size="x-small" color="warning">
      <FontAwesomeIcon
        :icon="['fak', 'seat']"
        class="mr-2 text-warning-accent"
      />
      <span
        >You have <b>{{ requests.length }}</b> pending
        {{ requests.length === 1 ? 'request' : 'requests' }}
      </span>
    </BittsTag>
  </BittsContainer>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsContainer,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';

import { computed } from 'vue';

import { SeatRequest } from '@/types/seat_requests';

import useTeamUpsellCTAs from '../../composables/team/useTeamUpsellCTAs';

const {
  title = '',
  requests = [],
  limit = null,
  count = 0,
} = defineProps<{
  title?: 'Full Access Seats' | 'Sales Seats' | 'Integration Seats';
  requests?: SeatRequest[];
  limit?: number | null;
  count?: number;
}>();

const emit = defineEmits<(e: 'open-add-seats-modal') => void>();

const isSales = computed(() => title === 'Sales Seats');
const isIntegration = computed(() => title === 'Integration Seats');
const isAtLimit = computed(() => limit !== null && limit === count);

const { getButtonInfo, getIntegrationButtonInfo } = useTeamUpsellCTAs();
const buttonInfo = computed(() =>
  isIntegration.value
    ? getIntegrationButtonInfo(isAtLimit.value)
    : getButtonInfo(
        isSales.value,
        isAtLimit.value,
        emit as (event: string) => void,
      ),
);
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.team-counter-card {
  @apply p-16 rounded-bts-lg text-neutral-text-strong flex flex-col gap-12 w-full;
}
h2 {
  @apply text-m font-bold;
}
.team-counter-card__title {
  @apply flex items-center justify-between;
}
</style>
